import React from "react"
import {useTranslation} from "react-i18next"
import {twMerge} from "tailwind-merge"

import {AKlaraDashboardSummary} from "../../../services/types.generated.ts"
import {customRangeDays, getPeriodRange, isLifetime} from "../period.ts"
import {useKlaraSummary} from "../useDashboardClock.ts"
import {useKlaraPeriod} from "../useKlaraPeriod.ts"
import {CardState} from "./CardState.tsx"
import {CARD_TITLE_CLASS_NAME, DELTA_PILL_CLASS_NAME, PILL_CLASS_NAME} from "./cardStyles.ts"
import {InfoDescription, InfoTooltip} from "./InfoTooltip.tsx"

export type TCoverageDelta = {direction: "up" | "down" | "same"; points: number} | null

export type TCoverageView = {
  /** Rounded percent; null when there are no open deals to cover. */
  percent: number | null
  /** Change in pts against the previous window; null when there is nothing to compare with. */
  delta: TCoverageDelta
  updated: number
  open: number
  target: number
}

/** The coverage card figures as returned by the backend; the delta is taken from the unrounded rates. */
export const coverageView = (summary: AKlaraDashboardSummary, isLifetime: boolean): TCoverageView => {
  const {rate, previous_rate: previousRate, updated, open} = summary.coverage

  return {
    percent: rate === null ? null : Math.round(rate * 100),
    delta: getCoverageDelta(rate, previousRate, isLifetime),
    updated,
    open,
    target: summary.coverage_target,
  }
}

const getCoverageDelta = (rate: number | null, previousRate: number | null, isLifetime: boolean): TCoverageDelta => {
  if (isLifetime || rate === null || previousRate === null) {
    return null
  }
  const points = Math.round(Math.abs(rate - previousRate) * 100)
  if (points === 0) {
    return {direction: "same", points}
  }
  return {direction: rate > previousRate ? "up" : "down", points}
}

/**
 * Share of the team's open deals with at least one Klara update in the period, against the company target.
 * It has no card of its own: the page shows it beside the updates chart in one card.
 */
export const CoverageCard: React.FC = () => {
  const {t} = useTranslation()
  const [period] = useKlaraPeriod()
  const titleId = React.useId()
  const infoId = React.useId()
  const {data, isPending, isError, isFetching, refetch} = useKlaraSummary()
  const summary = data?.data
  const view = summary && coverageView(summary, isLifetime(period))
  const range = React.useMemo(() => getPeriodRange(period), [period])
  const compare =
    range.from && range.to
      ? t("KlaraDashboard_Coverage_Compare", {count: customRangeDays(range.from, range.to)})
      : t("KlaraDashboard_Coverage_Delta_NoEarlierPeriod")

  return (
    <section aria-labelledby={titleId} className={"flex min-w-0 flex-[1_1_240px] flex-col"}>
      <div className={"flex items-center gap-2"}>
        <h2 id={titleId} className={CARD_TITLE_CLASS_NAME}>
          {t("KlaraDashboard_Coverage_Title")}
        </h2>
        <InfoTooltip name={t("KlaraDashboard_Coverage_Title")} descriptionId={infoId}>
          {t("KlaraDashboard_Coverage_Info")}
        </InfoTooltip>
      </div>
      <CardState query={{isPending, isError, isFetching, refetch}} skeletonClassName={"mt-3 h-32"} fill>
        {view && (
          <div className={"flex grow flex-col"}>
            <div className={"mt-3 flex flex-wrap items-baseline gap-2.5"}>
              <span
                data-testid={"coverage value"}
                className={"text-5xl leading-none font-extrabold tracking-[-2px] text-cr-blue-dark tabular-nums"}
              >
                {view.percent === null
                  ? t("KlaraDashboard_Coverage_NoValue")
                  : t("KlaraDashboard_Coverage_Value", {percent: view.percent})}
              </span>
              <CoverageDeltaPill delta={view.delta} />
            </div>
            <p className={"mt-2.5 text-[13px] text-cr-grey-80"}>
              {t("KlaraDashboard_Coverage_Line", {updated: view.updated, count: view.open})}
            </p>
            <CoverageBar percent={view.percent} target={view.target} compare={compare} />
          </div>
        )}
      </CardState>
      <InfoDescription id={infoId}>{t("KlaraDashboard_Coverage_Info")}</InfoDescription>
    </section>
  )
}

const CoverageDeltaPill: React.FC<{delta: TCoverageDelta}> = ({delta}) => {
  const {t} = useTranslation()

  const text = !delta
    ? t("KlaraDashboard_Kpi_Delta_None")
    : {
        up: t("KlaraDashboard_Coverage_Delta_Up", {points: delta.points}),
        down: t("KlaraDashboard_Coverage_Delta_Down", {points: delta.points}),
        same: t("KlaraDashboard_Coverage_Delta_Same"),
      }[delta.direction]

  return (
    <span
      data-testid={"coverage delta"}
      className={twMerge(
        PILL_CLASS_NAME,
        "px-2 py-[3px] text-xs",
        delta?.direction === "up"
          ? DELTA_PILL_CLASS_NAME.good
          : delta?.direction === "down"
            ? DELTA_PILL_CLASS_NAME.bad
            : DELTA_PILL_CLASS_NAME.neutral
      )}
    >
      {text}
    </span>
  )
}

const CoverageBar: React.FC<{percent: number | null; target: number; compare: string}> = ({
  percent,
  target,
  compare,
}) => {
  const {t} = useTranslation()

  return (
    // At the bottom, level with the chart's baseline, and at least 14px under the line above.
    <div className={"mt-auto pt-3.5"}>
      <div
        role={"progressbar"}
        aria-label={t("KlaraDashboard_Coverage_Title")}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent ?? undefined}
        className={"relative h-2 w-full rounded-full bg-cr-slate-8"}
      >
        <div
          data-testid={"coverage fill"}
          className={"h-full rounded-full bg-cr-blue"}
          style={{width: `${percent ?? 0}%`}}
        />
        <div
          data-testid={"coverage target"}
          aria-hidden={"true"}
          className={"absolute -top-1 h-4 w-0.5 -translate-x-1/2 rounded-[1px] bg-cr-blue-dark"}
          style={{left: `${target}%`}}
        />
      </div>
      <div className={"mt-1.5 flex flex-wrap justify-between gap-x-2 gap-y-0.5 text-[11px] text-cr-slate-60"}>
        <span data-testid={"coverage compare"}>{compare}</span>
        <span>{t("KlaraDashboard_Coverage_Target", {target})}</span>
      </div>
    </div>
  )
}
